import React from 'react';

const STATUS_ORDER = ['PLACED', 'PACKED', 'SHIPPED', 'DELIVERED', 'CANCELLED'];

const SummaryCard = ({ label, value, delta }) => (
  <div className="card metric-card">
    <div className="metric-label">{label}</div>
    <div className="metric-value">{value}</div>
    {delta ? <div className="metric-delta">{delta}</div> : null}
  </div>
);

const toDate = (value) => {
  const d = new Date(value);
  return Number.isFinite(d.getTime()) ? d : null;
};

const formatCurrency = (value) =>
  `${'\u20B9'}${Number(value || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;

const formatPercentDelta = (current, previous, label) => {
  if (previous > 0) {
    const pct = ((current - previous) / previous) * 100;
    const sign = pct >= 0 ? '+' : '';
    return `${sign}${pct.toFixed(1)}% ${label}`;
  }
  if (current > 0) return `New activity ${label}`;
  return `No change ${label}`;
};

const badgeClass = (status) => {
  const key = String(status || '').toLowerCase();
  const known = ['placed', 'packed', 'shipped', 'delivered', 'pending'];
  return known.includes(key) ? `badge-${key}` : 'status-badge';
};

const DashboardPage = ({ orders = [], products = [], users = [] }) => {
  const now = new Date();
  const startToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const thisMonthStart = new Date(now.getFullYear(), now.getMonth(), 1);
  const lastMonthStart = new Date(now.getFullYear(), now.getMonth() - 1, 1);

  const sevenDayStart = new Date(startToday);
  sevenDayStart.setDate(startToday.getDate() - 6);

  const prevSevenDayStart = new Date(sevenDayStart);
  prevSevenDayStart.setDate(sevenDayStart.getDate() - 7);

  const orderRows = (orders || [])
    .map((o) => ({ ...o, _date: toDate(o.created_at), _total: Number(o.total_amount || 0) }))
    .sort((a, b) => (b._date?.getTime?.() || 0) - (a._date?.getTime?.() || 0));

  const totalRevenue = orderRows.reduce((sum, o) => sum + o._total, 0);
  const ordersThisMonth = orderRows.filter((o) => o._date && o._date >= thisMonthStart);
  const ordersLastMonth = orderRows.filter((o) => o._date && o._date >= lastMonthStart && o._date < thisMonthStart);
  const revenueThisMonth = ordersThisMonth.reduce((sum, o) => sum + o._total, 0);
  const revenueLastMonth = ordersLastMonth.reduce((sum, o) => sum + o._total, 0);

  const ordersLast7Days = orderRows.filter((o) => o._date && o._date >= sevenDayStart);
  const ordersPrev7Days = orderRows.filter((o) => o._date && o._date >= prevSevenDayStart && o._date < sevenDayStart);
  const revenueLast7Days = ordersLast7Days.reduce((sum, o) => sum + o._total, 0);
  const revenuePrev7Days = ordersPrev7Days.reduce((sum, o) => sum + o._total, 0);

  const statusCounts = STATUS_ORDER.reduce((acc, status) => ({ ...acc, [status]: 0 }), {});
  orderRows.forEach((o) => {
    const status = String(o.status || 'PLACED').toUpperCase();
    if (statusCounts[status] !== undefined) statusCounts[status] += 1;
  });

  const cancelled = statusCounts.CANCELLED || 0;
  const delivered = statusCounts.DELIVERED || 0;
  const activeOrders = Math.max(0, orderRows.length - cancelled);
  const fulfillmentRate = activeOrders > 0 ? (delivered / activeOrders) * 100 : 0;
  const cancellationRate = orderRows.length > 0 ? (cancelled / orderRows.length) * 100 : 0;
  const avgOrderValue = orderRows.length ? totalRevenue / orderRows.length : 0;

  const outOfStock = (products || []).filter((p) => Number(p.quantity || 0) <= 0).length;
  const lowStock = (products || []).filter((p) => Number(p.quantity || 0) > 0 && Number(p.quantity || 0) < 20).length;
  const approvedRetailers = (users || []).filter((u) => Boolean(u.is_approved)).length;

  const dailySeries = Array.from({ length: 7 }, (_, idx) => {
    const d = new Date(sevenDayStart);
    d.setDate(sevenDayStart.getDate() + idx);
    const key = d.toDateString();
    const total = orderRows
      .filter((o) => o._date && o._date.toDateString() === key)
      .reduce((sum, o) => sum + o._total, 0);
    return {
      label: d.toLocaleDateString('en-IN', { weekday: 'short' }),
      value: total,
    };
  });
  const maxDailyRevenue = Math.max(1, ...dailySeries.map((x) => x.value));

  const topClientsMap = new Map();
  orderRows.forEach((o) => {
    const key =
      o.user_phone ||
      o.user_id ||
      o.user_email ||
      o.customer_phone ||
      o.customer_name ||
      'Unknown Client';
    const row = topClientsMap.get(key) || { name: String(key), orders: 0, revenue: 0, lastOrder: null };
    row.orders += 1;
    row.revenue += o._total;
    if (!row.lastOrder || (o._date && o._date > row.lastOrder)) row.lastOrder = o._date;
    topClientsMap.set(key, row);
  });
  const topClients = Array.from(topClientsMap.values())
    .sort((a, b) => b.revenue - a.revenue)
    .slice(0, 5);

  const inventoryRisk = (products || [])
    .filter((p) => Number(p.quantity || 0) < 20)
    .sort((a, b) => Number(a.quantity || 0) - Number(b.quantity || 0))
    .slice(0, 6);

  return (
    <div className="dashboard">
      <div className="card dashboard-header">
        <div>
          <h3>Business Snapshot</h3>
          <div className="muted">Updated {now.toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}</div>
        </div>
        <div className="row">
          <span className="status-badge">Total Orders: {orderRows.length}</span>
          <span className="badge-soft">Products: {products.length}</span>
          <span className="badge-soft">Retailers: {users.length}</span>
        </div>
      </div>

      <div className="dashboard-grid">
        <SummaryCard
          label="Total Revenue"
          value={formatCurrency(totalRevenue)}
          delta={formatPercentDelta(revenueThisMonth, revenueLastMonth, 'vs last month')}
        />
        <SummaryCard
          label="Last 7 Days Revenue"
          value={formatCurrency(revenueLast7Days)}
          delta={formatPercentDelta(revenueLast7Days, revenuePrev7Days, 'vs previous 7 days')}
        />
        <SummaryCard
          label="Average Order Value"
          value={formatCurrency(avgOrderValue)}
          delta={`${orderRows.length} orders`}
        />
        <SummaryCard
          label="Fulfillment Rate"
          value={`${fulfillmentRate.toFixed(1)}%`}
          delta={`${delivered} delivered / ${activeOrders} active`}
        />
        <SummaryCard
          label="Cancellation Rate"
          value={`${cancellationRate.toFixed(1)}%`}
          delta={`${cancelled} cancelled`}
        />
        <SummaryCard
          label="Inventory Attention"
          value={lowStock + outOfStock}
          delta={`${outOfStock} out of stock | ${lowStock} low stock`}
        />
        <SummaryCard
          label="Approved Retailers"
          value={approvedRetailers}
          delta={`${Math.max(0, users.length - approvedRetailers)} pending approval`}
        />
      </div>

      <div className="split">
        <div className="card chart-card">
          <div className="row" style={{ justifyContent: 'space-between' }}>
            <h3>Revenue Trend (Last 7 Days)</h3>
            <span className="muted">{formatCurrency(revenueLast7Days)}</span>
          </div>
          <div className="dashboard-bars">
            {dailySeries.map((entry) => (
              <div key={entry.label} className="dashboard-bar-item">
                <div className="dashboard-bar-wrap">
                  <div
                    className="dashboard-bar"
                    style={{ height: `${Math.max(8, (entry.value / maxDailyRevenue) * 100)}%` }}
                    title={`${entry.label}: ${formatCurrency(entry.value)}`}
                  />
                </div>
                <div className="dashboard-bar-value">{entry.value ? formatCurrency(entry.value) : '-'}</div>
                <div className="dashboard-bar-label">{entry.label}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="card chart-card">
          <h3>Order Status Distribution</h3>
          <div className="status-mix-list">
            {STATUS_ORDER.map((status) => {
              const count = statusCounts[status] || 0;
              const pct = orderRows.length ? (count / orderRows.length) * 100 : 0;
              return (
                <div key={status} className="status-mix-row">
                  <div className="row">
                    <span className={badgeClass(status)}>{status}</span>
                    <span className="muted">{count} orders</span>
                  </div>
                  <div className="status-mix-track">
                    <div className="status-mix-fill" style={{ width: `${pct}%` }} />
                  </div>
                  <div className="muted">{pct.toFixed(1)}%</div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div className="split">
        <div className="card">
          <div className="row" style={{ justifyContent: 'space-between' }}>
            <h3>Recent Orders</h3>
            <span className="muted">Latest 6</span>
          </div>
          <table className="table">
            <thead>
              <tr>
                <th>Order ID</th>
                <th>Customer</th>
                <th>Status</th>
                <th>Total</th>
                <th>Created</th>
              </tr>
            </thead>
            <tbody>
              {orderRows.slice(0, 6).map((o) => (
                <tr key={o._id}>
                  <td>{o._id}</td>
                  <td>{o.user_phone || o.user_id || 'N/A'}</td>
                  <td><span className={badgeClass(o.status)}>{o.status || 'PLACED'}</span></td>
                  <td>{formatCurrency(o._total)}</td>
                  <td>{o._date ? o._date.toLocaleDateString('en-IN') : '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {!orderRows.length ? <div className="muted" style={{ marginTop: 10 }}>No orders available.</div> : null}
        </div>

        <div className="card">
          <div className="row" style={{ justifyContent: 'space-between' }}>
            <h3>Top Clients</h3>
            <span className="muted">By revenue</span>
          </div>
          <table className="table">
            <thead>
              <tr>
                <th>Client</th>
                <th>Orders</th>
                <th>Revenue</th>
                <th>Last Order</th>
              </tr>
            </thead>
            <tbody>
              {topClients.map((client) => (
                <tr key={client.name}>
                  <td>{client.name}</td>
                  <td>{client.orders}</td>
                  <td>{formatCurrency(client.revenue)}</td>
                  <td>{client.lastOrder ? client.lastOrder.toLocaleDateString('en-IN') : '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {!topClients.length ? <div className="muted" style={{ marginTop: 10 }}>No client revenue data yet.</div> : null}
        </div>
      </div>

      <div className="card">
        <div className="row" style={{ justifyContent: 'space-between' }}>
          <h3>Inventory Attention</h3>
          <span className="muted">Lowest stock products</span>
        </div>
        <table className="table">
          <thead>
            <tr>
              <th>Product</th>
              <th>Brand</th>
              <th>Category</th>
              <th>Stock</th>
            </tr>
          </thead>
          <tbody>
            {inventoryRisk.map((p) => (
              <tr key={p._id || p.name}>
                <td>{p.name || 'Product'}</td>
                <td>{p.brand || '-'}</td>
                <td>{p.category || '-'}</td>
                <td>{Number(p.quantity || 0)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {!inventoryRisk.length ? <div className="muted" style={{ marginTop: 10 }}>No low stock risk found.</div> : null}
      </div>
    </div>
  );
};

export default DashboardPage;
